import { useAuth } from '../contexts/AuthContext';
import PremiumGate from '../components/PremiumGate';


/**
 * PLAN FEATURE MATRIX
 * Add new features here as columns; add new plans as rows.
 * Each key maps to a set of features that plan includes.
 *
 * Plans (in order of tier):
 *   free / crm                   → CRM only (no premium features)
 *   crm_voice                    → CRM + AI Voice
 *   crm_voice_website            → CRM + Voice + Website Builder
 *   crm_voice_website_marketing  → Full Suite (all features)
 */
const PLAN_FEATURES = {
  free: {
    aiVoice: false,
    websiteBuilder: false,
    aiMarketing: false,
  },
  crm: {
    aiVoice: false,
    websiteBuilder: false,
    aiMarketing: false,
  },
  crm_voice: {
    aiVoice: true,
    websiteBuilder: false,
    aiMarketing: false,
  },
  crm_voice_website: {
    aiVoice: true,
    websiteBuilder: true,
    aiMarketing: false,
  },
  crm_voice_website_marketing: {
    aiVoice: true,
    websiteBuilder: true,
    aiMarketing: true,
  },
  // Aliases
  full_suite: {
    aiVoice: true,
    websiteBuilder: true,
    aiMarketing: true,
  },
  growth: {
    aiVoice: true,
    websiteBuilder: true,
    aiMarketing: true,
  },
  enterprise: {
    aiVoice: true,
    websiteBuilder: true,
    aiMarketing: true,
  },
};

/**
 * Returns subscription status and feature access flags.
 *
 * Usage:
 *   const { canAccess, isTrialing, isPlatformAdmin, isOrgLoading } = useSubscription();
 *   if (isOrgLoading) return <LoadingSpinner />;
 *   if (!canAccess('aiVoice')) return <PremiumGate />;
 */
const useSubscription = () => {
  const { organization, profile, orgLoading } = useAuth();

  const isPlatformAdmin = !!profile?.is_platform_admin;

  // Platform admins bypass everything
  if (isPlatformAdmin) {
    return {
      isPlatformAdmin: true,
      isTrialing: false,
      isActive: true,
      isOrgLoading: false,
      planType: organization?.plan_type ?? null,
      subscriptionStatus: organization?.subscription_status ?? null,
      canAccess: () => true,
    };
  }

  // While org data is still loading, deny access (prevents flash of content)
  if (orgLoading) {
    return {
      isPlatformAdmin: false,
      isTrialing: false,
      isActive: false,
      isOrgLoading: true,
      planType: null,
      subscriptionStatus: null,
      canAccess: () => false,
    };
  }

  const subscriptionStatus = organization?.subscription_status;
  const trialEndDate = organization?.trial_end_date;
  const planType = organization?.plan_type;

  // Determine if on active paid subscription (Stripe 'active' or 'trialing' with a paid plan)
  const isActive = subscriptionStatus === 'active';

  // Determine if on free trial (not yet expired)
  const isTrialing =
    (subscriptionStatus === 'trial' || subscriptionStatus === 'trialing') &&
    trialEndDate &&
    new Date(trialEndDate) > new Date();

  // Resolve feature set based on plan_type ONLY
  // Trial users are NOT given blanket access — they must have a paid plan
  let features;
  if ((isActive || isTrialing) && planType) {
    // Normalize aliases
    const normalizedPlan =
      planType === 'full_suite' ? 'crm_voice_website_marketing' : planType;
    features = PLAN_FEATURES?.[normalizedPlan] ?? PLAN_FEATURES?.free;
  } else {
    // Expired / no subscription / free plan → no premium features
    features = PLAN_FEATURES?.free;
  }

  /**
   * Check if the current user can access a specific feature.
   * @param {string} featureKey - Key from PLAN_FEATURES (e.g. 'aiVoice', 'websiteBuilder', 'aiMarketing')
   * @returns {boolean}
   */
  const canAccess = (featureKey) => {
    return !!features?.[featureKey];
  };

  return {
    isPlatformAdmin,
    isTrialing,
    isActive,
    isOrgLoading: false,
    planType,
    subscriptionStatus,
    canAccess,
  };
};

export default useSubscription;
